// src/components/EditorErrorBoundary.tsx
// Error boundary for the editor route — catches unrecoverable errors and
// provides a user-friendly fallback with a retry button.
// Ported from Next.js app/editor/[projectId]/error.tsx.

import { Component, type ReactNode } from 'react'
import { createLogger } from '@/lib/logger'

const log = createLogger({ module: 'editor-page' })

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class EditorErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error) {
    log.error('Editor page error', error)
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-background px-4"
          role="alert"
        >
          <h2 className="text-xl font-semibold text-foreground">
            Something went wrong
          </h2>
          <p className="max-w-md text-center text-sm text-foreground-muted">
            The editor encountered an unexpected error. Your changes are safe —
            try reloading the page.
          </p>
          <button
            type="button"
            onClick={this.handleReset}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-foreground-on-primary transition-colors hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-ring"
          >
            Try again
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
